import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ShieldCheck, Loader2, Search } from "lucide-react";

export default function HallazgosCerradoresTab() {
  const { getAuthHeaders, user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [seleccion, setSeleccion] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isAdmin = ["administrador", "admin", "superadmin"].includes((user?.role || "").toLowerCase());

  useEffect(() => {
    const load = async () => {
      try {
        const [uRes, cRes] = await Promise.all([
          axios.get(`${API}/tickets/usuarios-disponibles`, getAuthHeaders()),
          axios.get(`${API}/hallazgos-auditoria/cerradores`, getAuthHeaders()),
        ]);
        setUsuarios(uRes.data || []);
        setSeleccion(cRes.data?.user_ids || []);
      } catch {
        toast.error("Error al cargar los usuarios");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) => {
    if (!isAdmin) return;
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const guardar = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/hallazgos-auditoria/cerradores`, { user_ids: seleccion }, getAuthHeaders());
      toast.success("Usuarios de cierre definitivo actualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const filtrados = usuarios.filter(
    (u) => (u.name || "").toLowerCase().includes(busqueda.toLowerCase())
      || (u.email || "").toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <div className="p-4 md:p-8" data-testid="cierre-definitivo-tab">
      <div className="max-w-3xl">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Cierre Definitivo</h2>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Selecciona los usuarios que pueden asignar el Estatus Final (Cerrado Conforme / No cumple) a los hallazgos resueltos.
          Los administradores siempre pueden hacerlo.
          {!isAdmin && " Solo un administrador puede modificar esta lista."}
        </p>
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar usuario..."
              className="pl-8 h-9"
              data-testid="cerradores-buscar-input"
            />
          </div>
          <Badge variant="outline" data-testid="cerradores-count-badge">{seleccion.length} seleccionados</Badge>
          {isAdmin && (
            <Button
              onClick={guardar}
              disabled={saving}
              className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white h-9"
              data-testid="cerradores-guardar-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          )}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando usuarios...</div>
        ) : (
          <div className="border rounded-lg divide-y dark:divide-slate-700 dark:border-slate-700 max-h-[28rem] overflow-y-auto bg-white dark:bg-slate-800">
            {filtrados.map((u) => (
              <label
                key={u.id}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm ${isAdmin ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50" : "cursor-default"}`}
                data-testid={`cerrador-row-${u.id}`}
              >
                <input
                  type="checkbox"
                  checked={seleccion.includes(u.id)}
                  onChange={() => toggle(u.id)}
                  disabled={!isAdmin}
                  className="w-4 h-4 accent-[#0C314C]"
                  data-testid={`cerrador-check-${u.id}`}
                />
                <span className="font-medium text-slate-800 dark:text-slate-200">{u.name}</span>
                <span className="text-xs text-slate-400">{u.email}</span>
                {seleccion.includes(u.id) && (
                  <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-300 border text-[10px]">Puede cerrar</Badge>
                )}
              </label>
            ))}
            {filtrados.length === 0 && (
              <div className="px-4 py-6 text-sm text-slate-500 text-center">Sin resultados</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
